import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE, PROXY_BASE } from '../config';
import {
  normalizeWord,
  wordsMatchFuzzy,
  isKitzur,
  isMemNunVariant,
  getRawTokens
} from '../utils/hebrewUtils';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  height: 100%;
  overflow: hidden;
  background-color: #fcfcfc;
`;

const Toolbar = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: #f5f6f8;
  border-bottom: 1px solid #e1e5ea;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  font-size: 0.95rem;
`;

const ScrollArea = styled.div`
  grid-column: 1 / -1;
  overflow-y: auto;
  padding: 40px 0;
  display: flex;
  flex-direction: column;
`;

const ChapterHeader = styled.h2`
  text-align: center;
  font-size: 1.5em;
  color: #666;
  margin-top: 3em;
  margin-bottom: 1.5em;
  font-family: 'SBL Hebrew', serif;
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(400px, 800px) 350px 1fr;
  column-gap: 40px;
  align-items: start;
  direction: rtl;
`;

const HalakhaContainer = styled.div`
  grid-column: 2;
  grid-row: 1;
  position: relative;
  margin-bottom: 1rem;
  padding: 15px 20px;
  border-radius: 8px;
  transition: background-color 0.2s;
  background-color: transparent;
  direction: rtl;
  font-family: 'SBL Hebrew', 'David', serif;
  font-size: 1.6rem;
  line-height: 1.8;
  color: #2c3e50;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const ToseftaWord = styled.span`
  background-color: ${props => props.isHighlighted ? '#fffbea' : 'transparent'};
  box-shadow: ${props => props.isHighlighted ? 'inset 0 -2px 0 0 #f0e6c0' : 'none'};
  transition: background-color 0.2s, box-shadow 0.2s;
`;

const MarginColumn = styled.div`
  grid-column: 3;
  grid-row: 1;
  padding-top: 15px;
  direction: rtl;
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HalakhaNumber = styled.span`
  color: #3498db;
  font-weight: bold;
  font-size: 0.6em;
  margin-left: 10px;
  user-select: none;
  position: relative;
  top: -5px;
`;

const MarginItem = styled.div`
  color: #95a5a6;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s;
  border-right: 3px solid transparent;
  padding-right: 10px;
  text-align: right;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;

  &:hover {
    color: #2c3e50;
    border-right-color: #bdc3c7;
  }

  ${props => props.active && `
    color: #2c3e50;
    border-right-color: #3498db;
    font-weight: 600;
  `}
`;

const StatusIcon = styled.span`
  font-size: 0.8em;
  color: ${props => props.type === 'saved' ? '#2e7d32' : '#f57c00'};
`;

const ParallelDisplay = styled.div`
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  direction: rtl;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Smooth scrolling */
  scroll-behavior: smooth;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ParallelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
`;

const ParallelTitle = styled.h4`
  margin: 0;
  color: #2c3e50;
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ParallelControls = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const SmallButton = styled.button`
  background: ${props => props.primary ? '#3498db' : 'white'};
  color: ${props => props.primary ? 'white' : '#495057'};
  border: 1px solid ${props => props.primary ? '#3498db' : '#ced4da'};
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #ced4da;
  padding: 3px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.75rem;
  color: #495057;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #e9ecef;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionLabel = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: #6c757d;
  font-size: 0.8rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 3px;
`;

const TextDisplay = styled.div`
  font-family: 'SBL Hebrew', serif;
  line-height: 1.6;
  font-size: ${props => props.compact ? '1.0rem' : '1.2rem'};
  direction: rtl;
`;

const Word = styled.span`
  display: inline-block;
  padding: 3px 4px;
  margin: 2px;
  border-radius: 3px;
  cursor: ${props => props.editable ? 'pointer' : 'default'};
  user-select: ${props => props.editable ? 'none' : 'text'};
  transition: all 0.15s;

  background-color: ${props => {
    if (props.isSelecting) return '#4dabf7';
    if (props.isInSelection) return '#a5d8ff';
    if (props.isSpan) return '#fffbea';
    return 'transparent';
  }};

  color: ${props => {
    if (props.isSelecting || props.isInSelection) return 'white';
    if (props.isShared) return '#b02a37';
    if (props.isContext) return '#999';
    return '#000';
  }};

  font-weight: ${props => props.isShared ? 'bold' : 'normal'};
  font-size: ${props => props.isContext ? '0.95em' : '1em'};
  border: ${props => props.isSpan ? '1px solid #f0e6c0' : 'none'};

  &:hover {
    background-color: ${props => {
      if (props.editable && !props.isSelecting && !props.isInSelection) return '#e9ecef';
      return props.isSelecting ? '#2196f3' : (props.isInSelection ? '#74c0fc' : props.isSpan ? '#fff3cd' : null);
    }};
  }
`;

const MetadataBadge = styled.span`
  background-color: ${props => props.color || '#e9ecef'};
  color: ${props => props.textColor || '#495057'};
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.7rem;
  margin-right: 4px;
  display: inline-block;
  margin-bottom: 4px;
`;

const MetadataContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  direction: ltr;
`;

function ViewerMode({ tractate, onTractateChange }) {
  const [tractates, setTractates] = useState([]);
  const [textData, setTextData] = useState(null);
  const [parallels, setParallels] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all');

  // Expansion state: Map of "refKey" -> parallel object
  const [expandedParallels, setExpandedParallels] = useState({});

  // Edit mode: Map of expansion key -> boolean
  const [editMode, setEditMode] = useState({});

  // Selection state: Map of expansion key -> { tosefta: {start, end}, parallel: {start, end} }
  const [selections, setSelections] = useState({});

  // Save status
  const [saveStatus, setSaveStatus] = useState({});

  // Text selection state: Map of refKey -> { start: wordIdx, end: wordIdx }
  const [textSelection, setTextSelection] = useState({});

  // For now, ignore any precomputed parallel span from the database and only
  // treat manual annotations or explicit parallel_spans as authoritative.
  // This forces the viewer to use the backend auto-span endpoint (dibur-based)
  // for default spans, while still respecting user edits.
  const hasParallelSpan = (p) => {
    if (!p) return false;
    if (p.parallel_spans && p.parallel_spans.length > 0) return true;
    if (p.manual_parallel_start_word !== null && p.manual_parallel_start_word !== undefined) return true;
    return false;
  };

  // Load available tractates for navigation
  useEffect(() => {
    axios.get(`${API_BASE}/api/tractates`)
      .then(res => setTractates(res.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
      if (tractate) {
      setExpandedParallels({});
      setEditMode({});
      setSelections({});
      setSaveStatus({});
      setTextSelection({});

      axios.get(`${API_BASE}/api/text/${tractate}`)
        .then(res => setTextData(res.data))
        .catch(err => console.error(err));

      axios.get(`${API_BASE}/api/parallels/${tractate}?include_text=false`)
        .then(res => {
            const dataList = res.data.entries || res.data.parallels;
            if (dataList) {
                setParallels(dataList.map((p, index) => ({ ...p, _uiId: index })));
            }
        })
        .catch(err => console.error(err));
    }
  }, [tractate]);

  // Handle text selection to filter parallels
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setTextSelection({});
        return;
      }

      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        setTextSelection({});
        return;
      }

      // Find the halakha container
      let node = range.commonAncestorContainer;
      while (node && !node.classList?.contains('halakha-container')) {
        node = node.parentNode;
      }

      if (!node) {
        setTextSelection({});
        return;
      }

      // Get the refKey from data attribute
      const refKey = node.getAttribute('data-ref');
      if (!refKey) {
        setTextSelection({});
        return;
      }

      // Get selected text and calculate word indices - use getRawTokens for consistent tokenization
      const halakhaText = node.textContent;
      const words = getRawTokens(halakhaText);
      const selectedText = selection.toString().trim();

      if (!selectedText) {
        setTextSelection({});
        return;
      }

      // Find the word indices of the selection
      const selectedWords = getRawTokens(selectedText);
      if (selectedWords.length === 0) {
        setTextSelection({});
        return;
      }

      // Simple approach: find first and last word of selection
      const firstWord = selectedWords[0];
      const lastWord = selectedWords[selectedWords.length - 1];

      let startIdx = -1;
      let endIdx = -1;

      for (let i = 0; i < words.length; i++) {
        if (startIdx === -1 && words[i].includes(firstWord)) {
          startIdx = i;
        }
        if (words[i].includes(lastWord)) {
          endIdx = i;
        }
      }

      if (startIdx !== -1 && endIdx !== -1 && endIdx >= startIdx) {
        setTextSelection({ [refKey]: { start: startIdx, end: endIdx } });
      } else {
        setTextSelection({});
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  // Apply source filter
  const filteredParallels = useMemo(() => {
    if (sourceFilter === 'all') return parallels;

    return parallels.filter(p => {
      const source = p.parallel_source_eng || '';
      return source === sourceFilter;
    });
  }, [parallels, sourceFilter]);

  const parallelsByHalakha = useMemo(() => {
    const map = {};
    filteredParallels.forEach((p) => {
        const parts = p.tosefta_ref.split(' ');
        const refNum = parts[parts.length - 1];
        if (!map[refNum]) map[refNum] = [];
        map[refNum].push(p);
    });
    return map;
  }, [filteredParallels]);

  const getExpansionKey = (refKey, parallelId) => `${refKey}-${parallelId}`;

  const getHalakhaText = (refKey) => {
    if (!textData || !textData.text) return "";
    try {
      const [c, h] = refKey.split(':').map(n => parseInt(n, 10));
      return textData.text[c-1]?.[h-1] || "";
    } catch(e) {
      return "";
    }
  };

  // Filter parallels based on text selection
  const getFilteredParallels = (refKey, parallels) => {
    const selection = textSelection[refKey];
    if (!selection) return parallels; // No selection, show all

    // Filter parallels that overlap with the selection
    return parallels.filter(p => {
      // Support both multiple spans and single span
      let spans = [];
      if (p.tosefta_spans) {
        spans = p.tosefta_spans;
      } else {
        const pStart = p.manual_tosefta_start_word ?? p.tosefta_start_word;
        const pEnd = p.manual_tosefta_end_word ?? p.tosefta_end_word;
        if (pStart !== null && pStart !== undefined && pEnd !== null && pEnd !== undefined) {
          spans = [{ start: pStart, end: pEnd }];
        }
      }

      if (spans.length === 0) {
        return false; // Skip parallels without defined spans
      }

      // Check if any span overlaps with selection
      // Overlap occurs if: span starts before selection ends AND span ends after selection starts
      return spans.some(span => span.start <= selection.end && span.end >= selection.start);
    });
  };

  // Adjust a backend-computed parallel span so that its start is aligned
  // with the dibur hamatchil in the client-side tokenization.
  const adjustParallelSpanToDibur = (parallel, origStart, origEnd) => {
    const dibur = parallel?.dibur_hamat_chil;
    const text = parallel?.parallel_text_raw || parallel?.parallel_text || "";
    if (!dibur || !text || origStart === null || origStart === undefined) {
      return { start: origStart, end: origEnd };
    }

    // Use getRawTokens for consistent tokenization with backend
    const words = getRawTokens(text);
    const diburWords = getRawTokens(dibur);
    if (diburWords.length === 0 || words.length === 0) {
      return { start: origStart, end: origEnd };
    }

    const maxIdx = words.length - diburWords.length;
    if (maxIdx < 0) {
      return { start: origStart, end: origEnd };
    }

    const searchRadius = 40;
    let bestIdx = origStart;
    const from = Math.max(0, origStart - searchRadius);
    const to = Math.min(maxIdx, origStart + searchRadius);

    for (let i = from; i <= to; i += 1) {
      let ok = true;
      for (let j = 0; j < diburWords.length; j += 1) {
        if (!wordsMatchFuzzy(words[i + j], diburWords[j])) {
          ok = false;
          break;
        }
      }
      if (ok) {
        bestIdx = i;
        break;
      }
    }

    const spanLen = (origEnd !== null && origEnd !== undefined && origEnd >= origStart)
      ? (origEnd - origStart)
      : 0;
    const newStart = bestIdx;
    const newEnd = spanLen > 0
      ? Math.min(words.length - 1, newStart + spanLen)
      : bestIdx;

    return { start: newStart, end: newEnd };
  };

  const ensureParallelSpan = (refKey, parallel) => {
    if (!parallel || hasParallelSpan(parallel)) return;
    const pid = parallel._uiId;

    axios.get(`${API_BASE}/api/parallels/${tractate}/${pid}/auto-span`)
      .then(res => {
        console.debug("auto-span", {
          refKey,
          parallel_ref: parallel.parallel_ref,
          result: res.data,
        });
        const updated = { ...parallel };
        const hasManualParallel = updated.manual_parallel_start_word !== null && updated.manual_parallel_start_word !== undefined;
        const hasManualTosefta = updated.manual_tosefta_start_word !== null && updated.manual_tosefta_start_word !== undefined;

        if (!hasManualParallel) {
          const adjusted = adjustParallelSpanToDibur(updated, res.data.target_start, res.data.target_end);
          updated.parallel_start_word = adjusted.start;
          updated.parallel_end_word = adjusted.end;
        }
        if (!hasManualTosefta) {
          updated.tosefta_start_word = res.data.source_start;
          updated.tosefta_end_word = res.data.source_end;
        }
        updated.confidence = res.data.score ?? updated.confidence;
        updated.auto_computed_span = true;

        setParallels(prev => prev.map(p => p._uiId === pid ? updated : p));
        setExpandedParallels(prev => ({ ...prev, [refKey]: updated }));
      })
      .catch(err => console.error(err));
  };

  const handleParallelClick = (refKey, parallel) => {
    if (expandedParallels[refKey]?._uiId === parallel._uiId) {
      // Collapse if already expanded
      const newExpanded = { ...expandedParallels };
      delete newExpanded[refKey];
      setExpandedParallels(newExpanded);
      return;
    }

    // Expand this parallel
    if (!parallel.parallel_text_raw && !parallel.parallel_text) {
      // Fetch parallel text
      const encodedRef = encodeURIComponent(parallel.parallel_ref);
      axios.get(`${PROXY_BASE}/proxy/sefaria/${encodedRef}`)
        .then(res => {
            const heText = res.data.he;
            let textContent = "";
            if (Array.isArray(heText)) textContent = heText.flat(Infinity).join(" ");
            else if (typeof heText === 'string') textContent = heText;

            const updatedP = { ...parallel, parallel_text_raw: textContent };
            setParallels(prev => prev.map(p => p._uiId === parallel._uiId ? updatedP : p));
            setExpandedParallels(prev => ({ ...prev, [refKey]: updatedP }));
            ensureParallelSpan(refKey, updatedP);
        })
        .catch(err => console.error(err));
    } else {
      setExpandedParallels(prev => ({ ...prev, [refKey]: parallel }));
      ensureParallelSpan(refKey, parallel);
    }
  };

  const handleNavigate = (refKey, direction) => {
    const halakhaParallels = parallelsByHalakha[refKey] || [];
    const currentParallel = expandedParallels[refKey];
    const currentIndex = halakhaParallels.findIndex(p => p._uiId === currentParallel._uiId);

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < halakhaParallels.length) {
      const newParallel = halakhaParallels[newIndex];
      handleParallelClick(refKey, newParallel);
    }
  };

  const toggleEditMode = (expKey) => {
    setEditMode(prev => ({ ...prev, [expKey]: !prev[expKey] }));
    if (!editMode[expKey]) {
      // Entering edit mode - initialize selection if not exists
      if (!selections[expKey]) {
        setSelections(prev => ({
          ...prev,
          [expKey]: { tosefta: { start: null, end: null }, parallel: { start: null, end: null } }
        }));
      }
    }
  };

  const handleWordClick = (expKey, type, idx) => {
    if (!editMode[expKey]) return;

    setSelections(prev => {
      const current = prev[expKey] || { tosefta: { start: null, end: null }, parallel: { start: null, end: null } };
      const typeSel = current[type];

      if (typeSel.start === null) {
        return {
          ...prev,
          [expKey]: { ...current, [type]: { start: idx, end: null } }
        };
      } else if (typeSel.end === null) {
        const start = Math.min(typeSel.start, idx);
        const end = Math.max(typeSel.start, idx);
        return {
          ...prev,
          [expKey]: { ...current, [type]: { start, end } }
        };
      } else {
        // Reset
        return {
          ...prev,
          [expKey]: { ...current, [type]: { start: idx, end: null } }
        };
      }
    });
  };

  const handleSave = (expKey, parallel) => {
    const sel = selections[expKey];
    if (!sel || sel.tosefta.start === null || sel.tosefta.end === null ||
        sel.parallel.start === null || sel.parallel.end === null) {
      return;
    }

    setSaveStatus(prev => ({ ...prev, [expKey]: 'saving' }));

    const payload = {
      tosefta_ref: parallel.tosefta_ref,
      parallel_ref: parallel.parallel_ref,
      tosefta_start_word: sel.tosefta.start,
      tosefta_end_word: sel.tosefta.end,
      parallel_start_word: sel.parallel.start,
      parallel_end_word: sel.parallel.end,
    };

    axios.post(`${API_BASE}/api/annotations`, payload)
      .then(res => {
        // Update parallel with new annotation
        setParallels(prev => prev.map(p => {
          if (p._uiId === parallel._uiId) {
            return {
              ...p,
              manual_tosefta_start_word: res.data.tosefta_start_word,
              manual_tosefta_end_word: res.data.tosefta_end_word,
              manual_parallel_start_word: res.data.parallel_start_word,
              manual_parallel_end_word: res.data.parallel_end_word,
              annotation_saved: true,
            };
          }
          return p;
        }));

        setSaveStatus(prev => ({ ...prev, [expKey]: 'saved' }));
        setEditMode(prev => ({ ...prev, [expKey]: false }));

        setTimeout(() => {
          setSaveStatus(prev => ({ ...prev, [expKey]: null }));
        }, 2000);
      })
      .catch(err => {
        console.error(err);
        setSaveStatus(prev => ({ ...prev, [expKey]: null }));
      });
  };

  const normalizeSpans = (rawSpans, wordCount) => {
    if (!Array.isArray(rawSpans)) return [];
    return rawSpans
      .map(s => {
        const start = Number.isFinite(s?.start) ? s.start : parseInt(s?.start, 10);
        const end = Number.isFinite(s?.end) ? s.end : parseInt(s?.end, 10);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(Math.max(0, end), wordCount - 1);
        if (clampedStart > clampedEnd || clampedStart >= wordCount) return null;
        return { start: clampedStart, end: clampedEnd };
      })
      .filter(Boolean)
      .sort((a, b) => a.start - b.start);
  };

  const ToseftaTextWithHighlight = ({ text, parallel, isEditing, expKey }) => {
    if (!text) return null;

    // Use getRawTokens for consistent tokenization with backend
    const words = getRawTokens(text);

    // Get span indices - support both single and multiple spans
    let spans = [];
    if (Array.isArray(parallel?.tosefta_spans) && parallel.tosefta_spans.length > 0) {
      // Multiple spans
      spans = parallel.tosefta_spans;
    } else {
      // Single span (backward compatibility)
      const spanStart = parallel?.manual_tosefta_start_word ?? parallel?.tosefta_start_word;
      const spanEnd = parallel?.manual_tosefta_end_word ?? parallel?.tosefta_end_word;
      if (spanStart !== null && spanStart !== undefined && spanEnd !== null && spanEnd !== undefined) {
        spans = [{ start: spanStart, end: spanEnd }];
      }
    }
    spans = normalizeSpans(spans, words.length);

    // Get selection state for editing
    const sel = selections[expKey]?.tosefta || { start: null, end: null };

    // Helper to check if word is in any span
    const isWordInSpan = (idx) => {
      return spans.some(span => idx >= span.start && idx <= span.end);
    };

    return (
      <>
        {words.map((word, idx) => {
          const isInSpan = isWordInSpan(idx) && !isEditing;

          let isSelecting = false;
          let isInSelection = false;

          if (isEditing && sel.start !== null) {
            if (idx === sel.start || idx === sel.end) {
              isSelecting = true;
            }
            if (sel.end !== null && idx >= sel.start && idx <= sel.end) {
              isInSelection = true;
            }
          }

          if (isEditing) {
            // In edit mode, use the Word component for click interaction
            return (
              <Word
                key={idx}
                isSpan={isInSpan}
                editable={true}
                isSelecting={isSelecting}
                isInSelection={isInSelection}
                onClick={() => handleWordClick(expKey, 'tosefta', idx)}
              >
                {word}
              </Word>
            );
          } else {
            // In view mode, use subtle highlight
            return (
              <ToseftaWord key={idx} isHighlighted={isInSpan}>
                {word}{' '}
              </ToseftaWord>
            );
          }
        })}
      </>
    );
  };

  // Render parallel text with multiple spans and ellipsis
  const renderParallelTextWithSpans = (text, parallel, expKey, otherText) => {
    if (!text) return null;

    // Use getRawTokens for consistent tokenization with backend
    const words = getRawTokens(text);

    // Get spans - support both single and multiple
    let spans = [];
    if (Array.isArray(parallel?.parallel_spans) && parallel.parallel_spans.length > 0) {
      spans = parallel.parallel_spans;
    } else {
      const spanStart = parallel?.manual_parallel_start_word ?? parallel?.parallel_start_word;
      const spanEnd = parallel?.manual_parallel_end_word ?? parallel?.parallel_end_word;
      if (spanStart !== null && spanStart !== undefined && spanEnd !== null && spanEnd !== undefined) {
        spans = [{ start: spanStart, end: spanEnd }];
      }
    }
    spans = normalizeSpans(spans, words.length);

    const isEditing = editMode[expKey];
    const sel = selections[expKey]?.parallel || { start: null, end: null };

    if (spans.length === 0) {
      if (!isEditing) {
        // Join words back together for display when no spans
        return <TextDisplay compact>{words.join(' ')}</TextDisplay>;
      }
      // In edit mode with no spans, render all words as selectable
      return (
        <TextDisplay compact>
          {words.map((word, idx) => (
            <Word
              key={idx}
              editable={true}
              isSelecting={idx === sel.start || idx === sel.end}
              isInSelection={sel.start !== null && sel.end !== null && idx >= sel.start && idx <= sel.end}
              onClick={() => handleWordClick(expKey, 'parallel', idx)}
            >
              {word}
            </Word>
          ))}
        </TextDisplay>
      );
    }

    const sortedSpans = spans;

    // Get other text words for shared word detection - use getRawTokens for consistent tokenization
    const otherWordsList = getRawTokens(otherText || '').map(w => normalizeWord(w)).filter(w => w.length > 0);
    const otherWordsSet = new Set(otherWordsList);

    const isWordShared = (word) => {
      const norm = normalizeWord(word);
      if (norm.length <= 1) return false;
      if (otherWordsSet.has(norm)) return true;
      return otherWordsList.some(o => isMemNunVariant(norm, o) || isKitzur(norm, o));
    };

    return (
      <TextDisplay compact>
        {sortedSpans.map((span, spanIdx) => (
          <React.Fragment key={spanIdx}>
            {spanIdx > 0 && <span style={{ color: '#999', margin: '0 8px' }}>...</span>}
            {words.slice(span.start, span.end + 1).map((word, wordIdx) => {
              const actualIdx = span.start + wordIdx;
              const isShared = isWordShared(word);

              let isSelecting = false;
              let isInSelection = false;

              if (isEditing && sel.start !== null) {
                if (actualIdx === sel.start || actualIdx === sel.end) {
                  isSelecting = true;
                }
                if (sel.end !== null && actualIdx >= sel.start && actualIdx <= sel.end) {
                  isInSelection = true;
                }
              }

              return (
                <Word
                  key={actualIdx}
                  isShared={isShared}
                  editable={isEditing}
                  isSelecting={isSelecting}
                  isInSelection={isInSelection}
                  onClick={() => isEditing && handleWordClick(expKey, 'parallel', actualIdx)}
                >
                  {word}
                </Word>
              );
            })}
          </React.Fragment>
        ))}
      </TextDisplay>
    );
  };

  const ParallelExpansion = ({ refKey, parallel }) => {
    const expKey = getExpansionKey(refKey, parallel._uiId);
    const halakhaText = getHalakhaText(refKey);
    const parallelText = parallel.parallel_text_raw || parallel.parallel_text || "";

    const halakhaParallels = parallelsByHalakha[refKey] || [];
    const currentIndex = halakhaParallels.findIndex(p => p._uiId === parallel._uiId);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < halakhaParallels.length - 1;

    const isEditing = editMode[expKey];
    const sel = selections[expKey];
    const canSave = isEditing && sel?.tosefta?.start !== null && sel?.tosefta?.end !== null &&
                    sel?.parallel?.start !== null && sel?.parallel?.end !== null;
    const saving = saveStatus[expKey] === 'saving';
    const saved = saveStatus[expKey] === 'saved';

    return (
      <ParallelDisplay>
        <ParallelHeader>
          <ParallelTitle>
            {parallel.parallel_source}: {parallel.parallel_display_ref}
          </ParallelTitle>
          <SmallButton onClick={() => {
            const newExpanded = { ...expandedParallels };
            delete newExpanded[refKey];
            setExpandedParallels(newExpanded);
          }}>
            ✕
          </SmallButton>
        </ParallelHeader>

        <MetadataContainer>
          {parallel.alignment_confidence !== null && parallel.alignment_confidence !== undefined && (
            <MetadataBadge
              color={parallel.alignment_confidence > 0.7 ? '#d4edda' : (parallel.alignment_confidence > 0.4 ? '#fff3cd' : '#f8d7da')}
              textColor={parallel.alignment_confidence > 0.7 ? '#155724' : (parallel.alignment_confidence > 0.4 ? '#856404' : '#721c24')}>
              Confidence: {(parallel.alignment_confidence * 100).toFixed(0)}%
            </MetadataBadge>
          )}

          {parallel.alignment_method && (
            <MetadataBadge color="#e7e7ff" textColor="#4a4a8a">
              {parallel.alignment_method}
            </MetadataBadge>
          )}

          {parallel.snapped_to_unit && (
            <MetadataBadge color="#d1ecf1" textColor="#0c5460">
              Unit Boundary
            </MetadataBadge>
          )}

          {parallel.auto_computed_span && (
            <MetadataBadge color="#fff3cd" textColor="#856404">
              Auto-aligned
            </MetadataBadge>
          )}

          {parallel.annotation_saved && (
            <MetadataBadge color="#d4edda" textColor="#155724">
              Manual ✓
            </MetadataBadge>
          )}
        </MetadataContainer>

        <ParallelControls style={{ marginBottom: '10px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <NavButton onClick={() => handleNavigate(refKey, 'prev')} disabled={!hasPrev}>
              ←
            </NavButton>
            <NavButton onClick={() => handleNavigate(refKey, 'next')} disabled={!hasNext}>
              →
            </NavButton>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <SmallButton onClick={() => toggleEditMode(expKey)}>
              {isEditing ? 'ביטול' : 'ערוך'}
            </SmallButton>
            {isEditing && (
              <SmallButton primary onClick={() => handleSave(expKey, parallel)} disabled={!canSave || saving}>
                {saving ? '...' : 'שמור'}
              </SmallButton>
            )}
          </div>
        </ParallelControls>

        {isEditing && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '3px',
            padding: '6px',
            marginBottom: '8px',
            fontSize: '0.7rem',
            color: '#856404'
          }}>
            <div>לחץ מילה ראשונה ואחרונה</div>
            <div style={{ marginTop: '4px' }}>
              ת: {sel?.tosefta?.start !== null ? `${sel.tosefta.start}${sel.tosefta.end !== null ? `-${sel.tosefta.end}` : '...'}` : '-'} |
              {' '}מ: {sel?.parallel?.start !== null ? `${sel.parallel.start}${sel.parallel.end !== null ? `-${sel.parallel.end}` : '...'}` : '-'}
            </div>
          </div>
        )}

        <TextSection>
          <SectionLabel>{parallel.parallel_source}</SectionLabel>
          {renderParallelTextWithSpans(parallelText, parallel, expKey, halakhaText)}
        </TextSection>

        {saved && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '3px',
            padding: '4px',
            marginTop: '8px',
            fontSize: '0.7rem',
            color: '#155724',
            textAlign: 'center'
          }}>
            נשמר!
          </div>
        )}
      </ParallelDisplay>
    );
  };

  if (!textData) return <div style={{padding: 40, textAlign: 'center'}}>Loading...</div>;
  if (textData.error) return <div style={{padding: 40, textAlign: 'center', color: 'red'}}>Error: {textData.error}</div>;

  return (
    <Container>
      <Toolbar>
        <div style={{ fontWeight: 600, color: '#2c3e50' }}>מסכת:</div>
        <Select
          value={tractate}
          onChange={(e) => onTractateChange && onTractateChange(e.target.value)}
        >
          {tractates.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>

        <div style={{ fontWeight: 600, color: '#2c3e50', marginLeft: '20px' }}>סינון:</div>
        <Select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          <option value="all">כל המקורות</option>
          <option value="Mishnah">משנה</option>
          <option value="Bavli">בבלי</option>
          <option value="Yerushalmi">ירושלמי</option>
          <option value="Tosefta">תוספתא</option>
          <option value="Tanakh">תנ״ך</option>
        </Select>
      </Toolbar>

      <ScrollArea>
          {textData.text && textData.text.map((chapter, cIdx) => (
            <React.Fragment key={cIdx}>
                <ChapterHeader>
                    פרק {cIdx + 1}
                </ChapterHeader>
                {Array.isArray(chapter) && chapter.map((halakha, hIdx) => {
                    const refKey = `${cIdx + 1}:${hIdx + 1}`;
                    const halakhaParallels = parallelsByHalakha[refKey] || [];
                    const filteredParallels = getFilteredParallels(refKey, halakhaParallels);
                    const expandedParallel = expandedParallels[refKey];

                    const expKey = expandedParallel ? getExpansionKey(refKey, expandedParallel._uiId) : null;
                    const isEditingCurrent = expKey ? editMode[expKey] : false;
                    const hasSelection = !!textSelection[refKey];

                    return (
                        <RowContainer key={hIdx}>
                            <HalakhaContainer className="halakha-container" data-ref={refKey}>
                                <HalakhaNumber>{hIdx + 1}</HalakhaNumber>
                                {expandedParallel ? (
                                  <ToseftaTextWithHighlight
                                    text={halakha}
                                    parallel={expandedParallel}
                                    isEditing={isEditingCurrent}
                                    expKey={expKey}
                                  />
                                ) : (
                                  <span dangerouslySetInnerHTML={{ __html: halakha }} />
                                )}
                            </HalakhaContainer>

                            <MarginColumn>
                                {!expandedParallel && filteredParallels.map(p => {
                                    return (
                                      <MarginItem
                                          key={p._uiId}
                                          onClick={() => handleParallelClick(refKey, p)}
                                      >
                                          {p.annotation_saved && <StatusIcon type="saved">✓</StatusIcon>}
                                          {!p.annotation_saved && (p.tosefta_start_word !== null && p.tosefta_start_word !== undefined) && (
                                            <StatusIcon type="warning">⚠</StatusIcon>
                                          )}
                                          {p.parallel_source}: {p.parallel_display_ref}
                                      </MarginItem>
                                    );
                                })}

                                {!expandedParallel && hasSelection && filteredParallels.length === 0 && (
                                  <div style={{
                                    padding: '8px',
                                    color: '#6c757d',
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic',
                                    textAlign: 'center'
                                  }}>
                                    אין מקבילות לטקסט שנבחר
                                  </div>
                                )}

                                {expandedParallel && (
                                  <ParallelExpansion refKey={refKey} parallel={expandedParallel} />
                                )}
                            </MarginColumn>
                        </RowContainer>
                    );
                })}
            </React.Fragment>
          ))}
      </ScrollArea>
    </Container>
  );
}

export default ViewerMode;
